import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function ForgotPasswordPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [step, setStep] = useState(1); // 1: email, 2: code verification, 3: new password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("Check your email", { description: "We've sent a reset code to your inbox" });
      setStep(2);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/verify-reset-code", { email: email.trim().toLowerCase(), code: code.trim() });
      toast.success("Code verified", { description: "Now set your new password" });
      setStep(3);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Code verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim().toLowerCase(), code: code.trim(), new_password: newPassword });
      toast.success("Password reset successful", { description: "You can now log in with your new password" });
      setStep(1);
      setEmail("");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="forgot-password-page">
      <div className="grid lg:grid-cols-2 gap-10 max-w-5xl w-full">
        <div className="hidden lg:flex flex-col justify-center">
          <Link to="/" className="font-display text-3xl font-black mb-6">
            Duo<span className="gold-text">capital</span>
          </Link>
          <h1 className="font-display text-4xl font-black leading-tight">
            Recover your <span className="gold-grad">access</span>.
          </h1>
          <p className="text-white/60 mt-4 max-w-md">
            Forgot your password? No worries. We'll help you reset it securely in just a few steps.
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm text-[#00D68F]">
            <ShieldCheck className="w-5 h-5" /> 100% secure • Encrypted process
          </div>
        </div>

        <form
          onSubmit={step === 1 ? requestReset : step === 2 ? verifyCode : resetPassword}
          className="glass-strong rounded-2xl p-8 space-y-5"
          data-testid="forgot-password-form"
        >
          <div className="flex items-center gap-2 mb-4">
            <Link to="/login" className="text-white/60 hover:text-white transition-colors" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <span className="text-xs text-white/60">Back to login</span>
          </div>

          {step === 1 && (
            <>
              <div className="lg:hidden">
                <Link to="/" className="font-display text-3xl font-black mb-2 block">
                  Duo<span className="gold-text">capital</span>
                </Link>
              </div>
              <div>
                <div className="label-eyebrow">Step 1 of 3</div>
                <h2 className="font-display text-3xl font-bold mt-2">Enter your email</h2>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@duocapital.com"
                  className="w-full px-4 py-3 rounded-lg"
                  data-testid="forgot-email"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="forgot-submit-email">
                {loading ? "Sending..." : "Send Reset Code"}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <div className="label-eyebrow">Step 2 of 3</div>
                <h2 className="font-display text-3xl font-bold mt-2">Verify your code</h2>
                <p className="text-sm text-white/60 mt-2">We sent a 6-digit code to {email}</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Verification Code</label>
                <input
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength="6"
                  className="w-full px-4 py-3 rounded-lg font-mono text-center text-lg tracking-widest"
                  data-testid="forgot-code"
                />
              </div>
              <div className="text-sm text-white/60">
                Didn't receive it? <button type="button" onClick={() => setStep(1)} className="text-[#D4AF37] hover:text-[#FCEB9E]">
                  Try again
                </button>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="forgot-submit-code">
                {loading ? "Verifying..." : "Verify Code"}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div>
                <div className="label-eyebrow">Step 3 of 3</div>
                <h2 className="font-display text-3xl font-bold mt-2">Set new password</h2>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-lg pr-12"
                    data-testid="forgot-new-password"
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors">
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-xs text-white/50 mt-2">At least 8 characters recommended</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-lg pr-12"
                    data-testid="forgot-confirm-password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors">
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="forgot-submit-password">
                {loading ? "Resetting..." : "Reset Password"}
              </button>
              <div className="text-sm text-center text-white/60">
                Remember your password? <Link to="/login" className="text-[#D4AF37]">
                  Sign in
                </Link>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
